import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { calculatePlanningMetrics } from '../../../services/planningFinance';
import { getProjectDisplayName } from '../../../utils/planningDisplay';

const ProjectCard = ({ project, property, onEdit, onDelete, compact = false }) => {
  const { formatCurrency } = useCurrency();

  const getStatusColor = (status) => {
    const colors = {
      planned: 'var(--color-muted-foreground)',
      in_progress: 'var(--color-primary)',
      on_hold: 'var(--color-warning)',
      completed: 'var(--color-success)',
      cancelled: 'var(--color-error)'
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const getStatusLabel = (status) => {
    const labels = {
      planned: 'Planifié',
      in_progress: 'En cours',
      on_hold: 'En attente',
      completed: 'Terminé',
      cancelled: 'Annulé'
    };
    return labels?.[status] || status;
  };

  const getProjectTypeLabel = (type) => {
    const labels = {
      renovation: 'Rénovation',
      expansion: 'Agrandissement',
      maintenance: 'Entretien',
      upgrade: 'Amélioration',
      marketing: 'Marketing',
      legal: 'Juridique',
      other: 'Autre'
    };
    return labels?.[type] || type;
  };

  const isCompleted = project?.status === 'completed';
  const hasActualCost = project?.actual_cost > 0 && parseFloat(project.actual_cost) > 0;
  const metrics = calculatePlanningMetrics(project, property, { useActualCost: hasActualCost });

  return (
    <div style={{
      background: 'var(--color-card)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border)',
      padding: 'var(--spacing-md)',
      transition: 'all var(--transition-base)',
      cursor: 'pointer'
    }}
    className="hover-lift"
    >
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 'var(--spacing-sm)'
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-foreground)',
            marginBottom: '4px'
          }}>
            {getProjectDisplayName(project, property)}
          </h3>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-xs)',
            marginBottom: 'var(--spacing-xs)'
          }}>
            <span style={{
              fontFamily: 'var(--font-caption)',
              fontSize: '0.75rem',
              color: 'var(--color-muted-foreground)',
              background: 'var(--color-muted)',
              padding: '2px 8px',
              borderRadius: 'var(--radius-sm)'
            }}>
              {getProjectTypeLabel(project?.project_type)}
            </span>
            <span style={{
              fontFamily: 'var(--font-caption)',
              fontSize: '0.75rem',
              color: getStatusColor(project?.status),
              background: `${getStatusColor(project?.status)}15`,
              padding: '2px 8px',
              borderRadius: 'var(--radius-sm)'
            }}>
              {getStatusLabel(project?.status)}
            </span>
          </div>
        </div>
      </div>

      {project?.description && (
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.875rem',
          color: 'var(--color-muted-foreground)',
          marginBottom: 'var(--spacing-sm)',
          lineHeight: 1.5
        }}>
          {project.description}
        </p>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 'var(--spacing-sm)',
        marginBottom: 'var(--spacing-md)',
        padding: 'var(--spacing-sm)',
        background: 'var(--color-muted)',
        borderRadius: 'var(--radius-md)'
      }}>
        <div>
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)', marginBottom: '2px' }}>Coût projet</p>
          <p style={{ fontFamily: 'var(--font-data)', fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-foreground)' }}>
            {formatCurrency(hasActualCost ? project?.actual_cost : project?.estimated_cost)}
          </p>
        </div>
        <div>
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)', marginBottom: '2px' }}>Valeur créée</p>
          <p style={{ fontFamily: 'var(--font-data)', fontSize: '0.875rem', fontWeight: 600, color: (metrics?.valueCreation ?? 0) > 0 ? 'var(--color-success)' : 'var(--color-foreground)' }}>
            {formatCurrency(metrics?.valueCreation ?? 0)}
          </p>
        </div>
        <div>
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)', marginBottom: '2px' }}>Capital net</p>
          <p style={{ fontFamily: 'var(--font-data)', fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-foreground)' }}>
            {formatCurrency(metrics?.netCost ?? 0)}
          </p>
        </div>
        <div>
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)', marginBottom: '2px' }}>Rend. réel</p>
          <p style={{ fontFamily: 'var(--font-data)', fontSize: '0.875rem', fontWeight: 600, color: (metrics?.realYield ?? 0) > 0 ? 'var(--color-success)' : 'var(--color-foreground)' }}>
            {metrics?.realYield != null ? `${(metrics.realYield * 100).toFixed(1)}%` : '—'}
          </p>
        </div>
        {metrics?.paybackYears != null && (
          <div style={{ gridColumn: '1 / -1' }}>
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)', marginBottom: '2px' }}>Délai récup.</p>
            <p style={{ fontFamily: 'var(--font-data)', fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-foreground)' }}>
              {metrics.paybackYears.toFixed(1)} an{metrics.paybackYears > 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {(project?.planned_start_date || project?.planned_end_date) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-xs)',
          marginBottom: 'var(--spacing-sm)',
          fontFamily: 'var(--font-caption)',
          fontSize: '0.75rem',
          color: 'var(--color-muted-foreground)'
        }}>
          <Icon name="Calendar" size={14} />
          <span>
            {project.planned_start_date && new Date(project.planned_start_date).toLocaleDateString('fr-CA')}
            {project.planned_start_date && project.planned_end_date && ' - '}
            {project.planned_end_date && new Date(project.planned_end_date).toLocaleDateString('fr-CA')}
          </span>
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: 'var(--spacing-xs)'
      }}>
        <Button
          variant="outline"
          size="sm"
          fullWidth
          iconName="Edit"
          iconPosition="left"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(project);
          }}
        >
          Modifier
        </Button>
        <Button
          variant="outline"
          size="sm"
          iconName="Trash2"
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) {
              onDelete(project?.id);
            }
          }}
        />
      </div>
    </div>
  );
};

export default ProjectCard;
